import React, { useState, useEffect, useMemo } from 'react';
import { adminApi } from '../../services/api';
import {
  Sparkles,
  Brain,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Cpu,
  Search,
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Platform-wide view of AI usage across firms: which provider each firm has
 * configured, whether their key actually works, and how far Knox has got
 * learning their writing style.
 *
 * The API deliberately returns booleans for key presence, never the keys
 * themselves, and never any draft content — see admin-ai-insights.js.
 */

const StatCard = ({ icon: Icon, label, value, sub, tone = 'default' }) => {
  const tones = {
    default: 'text-gray-900',
    good: 'text-green-600',
    warn: 'text-amber-600',
    bad: 'text-red-600',
  };
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-500">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-gray-400" />}
      </div>
      <div className={`mt-2 text-3xl font-bold ${tones[tone]}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
    </div>
  );
};

const ProviderBadge = ({ provider, configured }) => {
  if (!configured) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <XCircle className="h-3 w-3" />
        No key
      </span>
    );
  }
  const isClaude = provider === 'claude';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
      isClaude ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
    }`}>
      <CheckCircle2 className="h-3 w-3" />
      {isClaude ? 'Claude' : 'OpenAI'}
    </span>
  );
};

const ConfidenceBar = ({ value }) => {
  const percent = Math.round((value || 0) * 100);
  const colour =
    percent >= 80 ? 'bg-green-500' :
    percent >= 50 ? 'bg-blue-500' :
    percent >= 20 ? 'bg-amber-500' : 'bg-gray-400';
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="h-1.5 w-16 rounded-full bg-gray-200 overflow-hidden">
        <div className={`h-full rounded-full ${colour}`} style={{ width: `${percent}%` }} />
      </div>
      <span className="text-xs text-gray-600 tabular-nums">{percent}%</span>
    </div>
  );
};

const AiInsightsPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('edits');

  const fetchOverview = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const response = await adminApi.getAiOverview();
      setData(response.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load AI insights');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchOverview(); }, []);

  const firms = useMemo(() => {
    const rows = data?.firms || [];
    const term = search.trim().toLowerCase();
    const filtered = term
      ? rows.filter(f =>
          (f.firmName || '').toLowerCase().includes(term) ||
          (f.domain || '').toLowerCase().includes(term))
      : rows;

    const sorted = [...filtered];
    if (sortBy === 'edits') {
      sorted.sort((a, b) => (b.learning?.totalEdits || 0) - (a.learning?.totalEdits || 0));
    } else if (sortBy === 'confidence') {
      sorted.sort((a, b) => (b.learning?.maxConfidence || 0) - (a.learning?.maxConfidence || 0));
    } else {
      sorted.sort((a, b) => (a.firmName || '').localeCompare(b.firmName || ''));
    }
    return sorted;
  }, [data, search, sortBy]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const s = data?.summary || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-blue-600" />
            AI &amp; Knox Learning
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Which AI provider each firm is on, and how far Knox has learned their writing style.
            {data?.generatedAt && ` Generated ${new Date(data.generatedAt).toLocaleString()}.`}
          </p>
        </div>
        <button
          onClick={() => fetchOverview(true)}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {s.unreachableFirms > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            {s.unreachableFirms} firm{s.unreachableFirms === 1 ? '' : 's'} could not be reached.
            Their figures are excluded from the totals below — see the table for the reason.
          </p>
        </div>
      )}

      {/* Provider rollup */}
      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Provider configuration</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Cpu} label="Firms on OpenAI" value={s.providerSplit?.openai ?? 0}
            sub={`${s.firmsWithOpenaiKey ?? 0} have an OpenAI key saved`} />
          <StatCard icon={Cpu} label="Firms on Claude" value={s.providerSplit?.claude ?? 0}
            sub={`${s.firmsWithClaudeKey ?? 0} have a Claude key saved`} />
          <StatCard icon={XCircle} label="No usable key" value={s.providerSplit?.unconfigured ?? 0}
            tone={s.providerSplit?.unconfigured > 0 ? 'warn' : 'good'}
            sub="Selected a provider but saved no key for it" />
          <StatCard icon={CheckCircle2} label="Custom instructions" value={s.firmsWithPracticeInstructions ?? 0}
            sub={`of ${s.reachableFirms ?? 0} firms have written practice instructions`} />
        </div>
      </div>

      {/* Learning rollup */}
      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Knox learning</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Brain} label="Firms actively learning" value={s.learningActiveFirms ?? 0}
            sub={`${s.learningAvailableFirms ?? 0} firms migrated to schema v123`} />
          <StatCard icon={Brain} label="Edits captured" value={(s.totalEdits ?? 0).toLocaleString()}
            sub={`${(s.totalAnalysed ?? 0).toLocaleString()} analysed`} />
          <StatCard icon={Brain} label="Awaiting analysis" value={(s.totalPending ?? 0).toLocaleString()}
            tone={s.totalPending > 0 ? 'warn' : 'default'}
            sub={s.totalFailed > 0 ? `${s.totalFailed} failed analysis` : 'No failures'} />
          <StatCard icon={Brain} label="Avg draft kept"
            value={s.averageSimilarity != null ? `${Math.round(s.averageSimilarity * 100)}%` : '—'}
            sub="How much of Knox's draft survives editing (weighted by edits)" />
        </div>
      </div>

      {/* Per-firm table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-gray-700">By firm</h2>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="h-4 w-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search firms"
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="py-1.5 px-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="edits">Most edits</option>
              <option value="confidence">Highest confidence</option>
              <option value="name">Firm name</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Firm', 'Provider', 'AML provider', 'Edits', 'Analysed', 'Pending', 'Avg kept', 'Staff learning', 'Top confidence', 'Last edit'].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {firms.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                    No firms match that search.
                  </td>
                </tr>
              )}
              {firms.map((f) => {
                const usableKey = f.aiProvider === 'claude' ? f.claudeKeyConfigured : f.openaiKeyConfigured;
                const l = f.learning || {};
                return (
                  <tr key={f.tenantId} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{f.firmName || '—'}</div>
                      <div className="text-xs text-gray-500">{f.domain}</div>
                      {f.error && (
                        <div className="text-xs text-red-600 mt-0.5">Unreachable: {f.error}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <ProviderBadge provider={f.aiProvider} configured={usableKey} />
                      {f.claudeKeyConfigured && f.openaiKeyConfigured && (
                        <div className="text-xs text-gray-500 mt-1">both keys saved</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{f.amlAiProvider || <span className="text-gray-400">—</span>}</td>
                    {!l.available ? (
                      <td colSpan={7} className="px-4 py-3 text-xs text-gray-400">
                        {f.error ? '—' : 'Knox learning not yet migrated on this firm'}
                      </td>
                    ) : (
                      <>
                        <td className="px-4 py-3 tabular-nums text-gray-900">{l.totalEdits}</td>
                        <td className="px-4 py-3 tabular-nums text-gray-600">{l.analysedEdits}</td>
                        <td className="px-4 py-3 tabular-nums">
                          <span className={l.pendingAnalysis > 0 ? 'text-amber-600 font-medium' : 'text-gray-600'}>
                            {l.pendingAnalysis}
                          </span>
                          {l.failedAnalyses > 0 && (
                            <span className="ml-1 text-xs text-red-600">({l.failedAnalyses} failed)</span>
                          )}
                        </td>
                        <td className="px-4 py-3 tabular-nums text-gray-600">
                          {l.averageSimilarity != null ? `${Math.round(l.averageSimilarity * 100)}%` : '—'}
                        </td>
                        <td className="px-4 py-3 tabular-nums text-gray-600">{l.staffLearning}</td>
                        <td className="px-4 py-3"><ConfidenceBar value={l.maxConfidence} /></td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {l.lastEditAt ? new Date(l.lastEditAt).toLocaleDateString() : '—'}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Model reference */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-700">Models in use</h2>
          <p className="mt-1 text-xs text-gray-500">
            Models are pinned in the worker code per feature, not chosen per firm. A firm's
            provider setting decides which column below applies to them.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Feature', 'OpenAI model', 'Claude model', 'Source'].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(data?.models || []).map((m) => (
                <tr key={m.feature} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-gray-900">{m.feature}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-600">
                    {m.openai || <span className="text-gray-400 font-sans">not supported</span>}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-600">
                    {m.claude || <span className="text-gray-400 font-sans">not supported</span>}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-400">{m.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-gray-400">
        API keys are never returned by this endpoint — only whether one is saved. No draft or
        client content is read; the learning figures are counts and averages only.
      </p>
    </div>
  );
};

export default AiInsightsPage;
